import { useEffect, useState, useRef, useCallback } from 'react';
import type { FreshchatUserConfig } from '../types/freshchat';
import { freshchatService } from '../services/freshchat-service';
import type { IdentityUser } from '@/features/user/types/user';
import { useUser } from '@/features/user/hooks/useUser';
import { useLogger } from '@/lib/logger';

interface UseFreshchatOptions {
  autostart?: boolean;
}

interface UseFreshchatReturn {
  isLoaded: boolean;
  isInitialized: boolean;
  showWidget: () => Promise<void>;
  updateUserData: (userConfig: FreshchatUserConfig) => void;
  reboot: (userConfig: FreshchatUserConfig) => void;
  stop: () => Promise<void>;
  start: (cfg?: FreshchatUserConfig) => Promise<void>;
}

export const useFreshchat = (
  options?: UseFreshchatOptions
): UseFreshchatReturn => {
  const { autostart = false } = options || {};
  const { user } = useUser();
  const logger = useLogger();
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const startedRef = useRef(false);

  // Convert user to FreshchatUserConfig
  const getUserConfig = useCallback(
    (
      userData: IdentityUser | null | undefined
    ): FreshchatUserConfig | undefined => {
      if (!userData) {
        return undefined;
      }

      return {
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email,
        phone: userData.phone,
        externalId: userData.id,
      };
    },
    []
  );

  const updateUserData = useCallback(
    (newUserConfig: FreshchatUserConfig): void => {
      try {
        freshchatService.updateFreshchat(newUserConfig);
      } catch (error) {
        logger.error('Error updating user data', {
          error: error instanceof Error ? error.message : String(error),
        });
      }
    },
    [logger]
  );

  // Autostart freshchat with cleanup
  useEffect(() => {
    let cancelled = false;

    if (autostart && !startedRef.current) {
      startedRef.current = true;
      const userConfig = getUserConfig(user);
      freshchatService.start(userConfig).catch((error) => {
        if (!cancelled) {
          logger.error('Error starting freshchat', {
            error: error instanceof Error ? error.message : String(error),
          });
        }
      });
    }

    return () => {
      cancelled = true;
      if (autostart) {
        startedRef.current = false;
      }
    };
  }, [autostart, user, getUserConfig]);

  // Track user changes and update freshchat (only when initialized)
  useEffect(() => {
    if (isInitialized && user) {
      const userConfig = getUserConfig(user);
      if (userConfig) {
        freshchatService.updateFreshchat(userConfig);
      }
    }
  }, [isInitialized, user, getUserConfig]);

  // Monitor loading and initialization state using requestAnimationFrame
  useEffect(() => {
    let cancelled = false;

    const check = () => {
      if (cancelled) {
        return;
      }

      const loaded = freshchatService.isLoaded();
      const initialized = freshchatService.isInitialized();

      setIsLoaded((prev) => (prev !== loaded ? loaded : prev));
      setIsInitialized((prev) => (prev !== initialized ? initialized : prev));

      if (!loaded || !initialized) {
        requestAnimationFrame(check);
      }
    };

    check();

    return () => {
      cancelled = true;
    };
  }, []);

  const showWidget = useCallback(async (): Promise<void> => {
    await freshchatService.showWidget();
  }, []);

  const reboot = useCallback((newUserConfig: FreshchatUserConfig): void => {
    freshchatService.reboot(newUserConfig);
  }, []);

  const stop = useCallback(async (): Promise<void> => {
    await freshchatService.stop();
    setIsLoaded(false);
    setIsInitialized(false);
    startedRef.current = false;
  }, []);

  const start = useCallback(
    async (cfg?: FreshchatUserConfig): Promise<void> => {
      try {
        await freshchatService.start(cfg);
      } catch (error) {
        logger.error('Error starting freshchat', {
          error: error instanceof Error ? error.message : String(error),
        });
      }
    },
    []
  );

  return {
    isLoaded,
    isInitialized,
    showWidget,
    updateUserData,
    reboot,
    stop,
    start,
  };
};
